/**
 * Sticky landing header.
 *
 * Becomes translucent + blurred once the user scrolls past 8px so the
 * hero peeks through. Section anchors (Features / How it works / FAQ)
 * smooth-scroll via `scroll-behavior: smooth` on the page.
 */
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Logo } from '@/components/ui/Logo'
import { Button } from '@/components/ui/Button'
import { LanguageSwitcher } from '@/components/ui/LanguageSwitcher'
import { useAuthStore } from '@/stores/auth'
import { cn } from '@/lib/cn'

export function PublicHeader() {
  const { t } = useTranslation(['landing', 'common'])
  const isAuthed = useAuthStore((s) => !!s.accessToken)
  const [scrolled, setScrolled] = useState(false)

  useEffect(() => {
    function onScroll() { setScrolled(window.scrollY > 8) }
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  return (
    <header
      className={cn(
        'sticky top-0 z-30 border-b transition-colors',
        scrolled
          ? 'bg-ink/80 border-line backdrop-blur-md'
          : 'bg-transparent border-transparent',
      )}
    >
      <div className="max-w-6xl mx-auto px-6 h-14 flex items-center">
        <Link to="/" className="hover:opacity-90">
          <Logo />
        </Link>

        {/* Section anchors — hidden on small screens */}
        <nav className="ml-8 hidden md:flex items-center gap-6 text-sm text-textDim">
          <a href="#features"     className="hover:text-text transition-colors">{t('landing:nav.features')}</a>
          <a href="#how-it-works" className="hover:text-text transition-colors">{t('landing:nav.how_it_works')}</a>
          <a href="#faq"          className="hover:text-text transition-colors">{t('landing:nav.faq')}</a>
        </nav>

        <div className="ml-auto flex items-center gap-3">
          <LanguageSwitcher />
          {isAuthed ? (
            <Link to="/app">
              <Button variant="secondary" size="sm">
                {t('common:actions.open_app')}
              </Button>
            </Link>
          ) : (
            <>
              <Link to="/login">
                <Button variant="ghost" size="sm">
                  {t('common:actions.log_in')}
                </Button>
              </Link>
              <Link to="/register">
                <Button variant="primary" size="sm">
                  {t('common:actions.sign_up')}
                </Button>
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  )
}
